import React, { useEffect, useState } from 'react';
import { Button, Stack, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import WelcomePackets from './WelcomePackets';
import useEditBonusWelcome from '../../../../../hooks/site/bonus/useEditBonusWelcome';
import { IconPlus, IconX } from '@tabler/icons-react';
import { dispatchCusEvent, returnNimMaxNumber } from '../../../../../utils';
import { useAppSelector } from '../../../../../store';
import { ENUM_CUS_EVENT } from '../../../../../enums/enums';
import SubgroupSave from '../../../../../components/bonus/ui/SubgroupSave';
import useBootstrapSize from '../../../../../hooks/useBootstrapSize';
import CusCheckbox from '../../../../../components/material-ui/CusCheckbox';
import FlexContainer from '../../../../../components/material-ui/adaptive/FlexContainer';
import ShowContainer from '../../../../../components/material-ui/ShowContainer';
import Card from '../../../../../components/material-ui/adaptive/Card';
import CusInput from '../../../../../components/material-ui/CusInput';
import { ENUM_BONUS_ID } from '../../../../../enums/MenuItems';

export default function Welcome({ groupId, group }) {
  const { t } = useTranslation();
  const { isMobile, isTablet, isMiniDesktop, isDesktop, isLessTablet } = useBootstrapSize();
  const campaignByGroupId = useAppSelector((state) => state.freeSpin.campaignByGroupId);

  const {
    welcomeEnable,
    updateSubgroups,
    expireDays,
    packets,
    autoActive,
    lifetimeDays,
    saveDisabled,
    useCasinoTax,
    lockSetting,
    selectCurrency,
    activeBonus,
    setActiveBonus,
    setSelectCurrency,
    changeLockSetting,
    changeUseCasinoTax,
    changeLifetimeDays,
    changeSubgroup,
    changeEnable,
    getPackageInfo,
    changeFieldPackage,
    addNewPackage,
    onDeleteRow,
    onDeletePackage,
    updateExpireDays,
    onSave
  } = useEditBonusWelcome(groupId);

  const lengthPackage = packets?.length || 0;
  const isManyPackage =
    isMobile ||
    (isTablet && lengthPackage > 4) ||
    (isMiniDesktop && lengthPackage > 6) ||
    (isDesktop && lengthPackage > 8);

  const onTabChange = (_, newValue) => setActiveBonus(newValue);
  const onNewTabClick = () => {
    addNewPackage();
    setActiveBonus(lengthPackage || 0);
  };

  const onDeleteTab = (e, indx) => {
    e.preventDefault();
    e.stopPropagation();
    onDeletePackage(indx);

    if (activeBonus === indx) setActiveBonus(0);
    if (activeBonus > indx) setActiveBonus(activeBonus - 1);
    if (activeBonus < indx) setActiveBonus(activeBonus);
  };

  useEffect(() => {
    if (Object.keys(campaignByGroupId).length) return;
    dispatchCusEvent(ENUM_CUS_EVENT.getFreespinList, { groupId });
  }, [campaignByGroupId]);

  return (
    <>
      <FlexContainer
        justifyContent={'space-between'}
        flexDirection={isManyPackage ? 'column' : undefined}>
        <CusCheckbox title="Enable" checked={welcomeEnable} onChange={changeEnable} />

        <Stack display={'flex'} gap={1} flexDirection={isManyPackage ? 'column' : 'row'}>
          <Tabs
            value={activeBonus}
            onChange={onTabChange}
            variant="scrollable"
            scrollButtons="auto">
            {packets?.map((_, indx) => (
              <Tab
                key={indx}
                icon={
                  <ShowContainer condition={indx}>
                    <span
                      onClick={(e) => onDeleteTab(e, indx)}
                      style={{
                        width: '20px',
                        height: '20px',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer'
                      }}>
                      <IconX
                        style={{
                          width: '16px',
                          height: '16px',
                          top: 0,
                          color: 'black'
                        }}
                      />
                    </span>
                  </ShowContainer>
                }
                iconPosition="end"
                value={indx}
                style={{ fontWeight: 600 }}
                label={t('Bonus #{{indx}}', { indx: indx + 1 })}
              />
            ))}
          </Tabs>
          <Stack
            style={{ cursor: 'pointer' }}
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}>
            <Button onClick={onNewTabClick} fullWidth={isManyPackage}>
              {packets?.length ? <IconPlus /> : t('Add bonus')}
            </Button>
          </Stack>
        </Stack>
      </FlexContainer>
      <ShowContainer condition={packets?.[activeBonus]}>
        <Card variant="outlined">
          <WelcomePackets
            bonus={packets?.[activeBonus]}
            id={activeBonus}
            group={group}
            selectCurrency={selectCurrency}
            getPackageInfo={getPackageInfo}
            changeFieldPackage={changeFieldPackage}
            onDelete={onDeleteRow}
            setSelectCurrency={setSelectCurrency}
          />
        </Card>
      </ShowContainer>

      <ShowContainer condition={packets?.length}>
        <Stack display={'flex'} flexDirection={'column'} mt={2} gap={isLessTablet ? 1 : 0.5}>
          <CusInput
            label={
              'days, the validity period of the Welcome bonus package, from the moment of player registration'
            }
            type="number"
            value={+expireDays || 1}
            onChange={(e) => updateExpireDays(returnNimMaxNumber(e.target.value, 1, 365))}
            inputProps={{ min: 1, max: 365 }}
            isRowReverse={!isLessTablet}
          />

          <CusInput
            label={'days, the lifetime of bonuses credited to the bonus balance.'}
            subLabel={'After which they will be deleted if they are not wagered.'}
            type="number"
            value={lifetimeDays ?? 0}
            onChange={(e) => changeLifetimeDays(+e.target.value)}
            inputProps={{ min: 0 }}
            isRowReverse={!isLessTablet}
          />

          <CusCheckbox
            label="In bonus calculations, reduce the deposit amount by the amount of the commission withheld from the player for the deposit."
            checked={useCasinoTax}
            onChange={changeUseCasinoTax}
          />
        </Stack>
      </ShowContainer>

      <SubgroupSave
        lockSetting={lockSetting}
        updateSubgroups={updateSubgroups}
        saveDisabled={saveDisabled}
        id={ENUM_BONUS_ID.welcome}
        showLockSetting={true}
        isNodeGroup={!group?.game}
        showUpdate={!group?.game}
        onSave={onSave}
        changeSubgroup={changeSubgroup}
        changeLockSetting={changeLockSetting}
      />
    </>
  );
}
